import { createApp, h, useCssModule } from "vue";
import { createInertiaApp } from "@inertiajs/vue3";
import { createPinia } from "pinia";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import { createI18n } from "vue-i18n";
import { es as vuetifyEs, en as vuetifyEn } from "vuetify/locale";

// Components
import BotonDinamico from "./components/BotonDinamico.vue";
import EstatusFormas from "./components/EstatusFormas.vue";
import EstatusManiobras from "./components/EstatusManiobras.vue";
import EstatusColgantePabellon from "./components/EstatusColgantePabellon.vue";
import EstatusDecoradorSolicitud from "./components/EstatusDecoradorSolicitud.vue";
import EstatusAfiliadas from "./components/EstatusAfiliadas.vue";
import CabeceraFormaDinamica from "./components/CabeceraFormaDinamica.vue";
import CabeceraCard from "./components/CabeceraCard.vue";
import StandInfo from "./components/StandInfo.vue";
import ProveedorCardBar from "./components/ProveedorCardBar.vue";
import CampoTextoLectura from "./components/CampoTextoLectura.vue";
import ListadoArchivos from "./components/ListadoArchivos.vue";
import FormGroup from "./components/FormGroup.vue";
import FormGroupHint from "./components/FormGroupHint.vue";
import DialogoConfirmacion from "./components/DialogoConfirmacion.vue";
import Facturacion from "./components/Facturacion.vue";
import PagoEnvio from "./components/PagoEnvio.vue";
import GestorPagos from "./components/GestorPagos.vue";
import ScrollToTopFab from "./components/ScrollToTopFab.vue";
import Layout from "./Layout/Layout.vue";
import AppBar from "./Layout/AppBar/AppBar.vue";
import Navigation from "./Layout/Navigation.vue";
// import CountryFlag from "vue-country-flag";

// Language
import esMessages from "./lang/es.json";
import enMessages from "./lang/en.json";

// 🌍 Vue I18n
const numberFormats = {
  "en-US": {
    currency: {
      style: "currency",
      currency: "USD",
      currencyDisplay: "code",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    },
    integer: {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    },
    percent: { style: "percent", useGrouping: false },
  },
  "es-MX": {
    currency: {
      style: "currency",
      currency: "MXN",
      currencyDisplay: "code",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: { style: "percent", useGrouping: false },
  },
};

const datetimeFormats = {
  "en-US": {
    short: { year: "numeric", month: "short", day: "numeric" },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
    },
  },
  "es-MX": {
    short: { day: "numeric", month: "short", year: "numeric" },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    },
  },
};

const messages = {
  "en-US": enMessages,
  "es-MX": esMessages,
  en: enMessages,
  es: esMessages,
};

// 📦 Pinia
const pinia = createPinia();

// 🚀 Inertia + Vue 3 app
createInertiaApp({
  resolve: (name) => import(`./Pages/${name}.vue`),
  setup({ el, App, props, plugin }) {
    // 🌍 Get locale from Laravel via Inertia props
    const laravelLocale = props.initialPage.props.locale || "es";

    // Map Laravel locale to Vuetify locale (es/en)
    const vuetifyLocale =
      laravelLocale === "es" || laravelLocale === "es-MX" ? "es" : "en";

    // Map Laravel locale to i18n locale with region (es-MX/en-US)
    const i18nLocale =
      laravelLocale === "es" || laravelLocale === "es-MX" ? "es-MX" : "en-US";

    // 🎨 Vuetify config with locale
    const vuetify = createVuetify({
      components,
      directives,
      locale: {
        locale: vuetifyLocale,
        fallback: "en",
        messages: { es: vuetifyEs, en: vuetifyEn },
      },
      theme: {
        defaultTheme: "light",
        themes: {
          light: {
            colors: {
              text: "#333333",
              textLight: "#646464",
              textDarken: "#35383A",
              primary: "#0080C5",
              primaryLight: "#e3ecf7",
              primaryDark: "#0063a9",
              secondary: "#09B582",
              secondaryLight: "#EBF8F4",
              secondaryDarken: "#005799",
              tertiary: "#F37327",
              tertiaryLight: "#FEF7E9",
              tertiaryDark: "#f7942d",
              accent: "#EA5D6B",
              accentLight: "#F7E8EA",
              accent2: "#005799",
              accent2Dark: "#717a01",
              background: "#F4F4F5",
              background2: "#ECECEC",
              background3: "#efefef",
              backgroundLight: "#F9F9F9",
              backgroundDark: "#e0dede",
              fieldBackground: "#EDEDED",
              borderLight: "#E1E3E1",
              grey: "#545353",
              greyDark: "#424242",

              warning: "#ffd334",
              white: "#FFFFFF",
              black: "#000000",
            },
          },
        },
      },
    });

    const i18nMessages = {
      ...messages,
      ...(i18nLocale === "es" || i18nLocale === "es-MX"
        ? { "es-MX": messages.es }
        : {}),
      ...(i18nLocale === "en" || i18nLocale === "en-US"
        ? { "en-US": messages.en }
        : {}),
    };

    // Create i18n instance with dynamic locale
    const i18n = createI18n({
      legacy: false, // para usar Composition API
      locale: i18nLocale, // Use es-MX for Spanish to get Mexican number format
      fallbackLocale: "en",
      messages: i18nMessages,
      numberFormats,
      datetimeFormats,
      warnHtmlMessage: false, // Desactivar advertencias sobre HTML en mensajes
    });

    // Set Vuetify locale based on app locale
    //const vuetifyLocale = locale === "es" ? "es" : locale === "en" ? "en" : locale;
    // vuetify.locale.current = vuetifyLocale;

    const app = createApp({ render: () => h(App, props) });

    app.use(plugin).use(pinia).use(vuetify).use(i18n);

    // ✅ Registro global de componentes
    app.component("boton-dinamico", BotonDinamico);
    app.component("estatus-formas", EstatusFormas);
    app.component("estatus-maniobras", EstatusManiobras);
    app.component("estatus-colgante-pabellon", EstatusColgantePabellon);
    app.component("estatus-afiliadas", EstatusAfiliadas);
    app.component("estatus-decorador-solicitud", EstatusDecoradorSolicitud);
    app.component("cabecera-forma-dinamica", CabeceraFormaDinamica);
    app.component("cabecera-card", CabeceraCard);
    app.component("stand-info", StandInfo);
    app.component("proveedor-card-bar", ProveedorCardBar);
    app.component("campotexto-lectura", CampoTextoLectura);
    app.component("listado-archivo", ListadoArchivos);
    app.component("form-group", FormGroup);
    app.component("form-group-hint", FormGroupHint);
    app.component("dialogo-confirmacion", DialogoConfirmacion);
    app.component("facturacion", Facturacion);
    app.component("pago-envio", PagoEnvio);
    app.component("gestor-pagos", GestorPagos);
    app.component("scroll-to-top-fab", ScrollToTopFab);
    app.component("layout", Layout);
    app.component("navigation", Navigation);
    app.component("app-bar", AppBar);

    app.mixin({ methods: { route } }).mount(el);
  },
});
